import { CommunityRelationshipType, CustomListKey, IPlacement } from "@sp-crm/core";
import { NgBylinePill } from "components/byline/ng-byline-pill";
import React, { useMemo } from "react";
import { ResponsiveMode } from "store/reducers/responsive";
import { useCustomList } from "store/selectors/bridge";
import { useResponsiveMode } from "store/selectors/hooks";
import { Subpage } from ".";
import { ActionBarAssignedTo } from "./action-bar-assigned-to";
import { ActionBarDateComponent } from "./action-bar-date-component";
import { ActionBarMoveCommunity } from "./action-bar-move-community";
import { ActionBarStatus } from "./action-bar-status";
import { InvoiceSummary } from "./invoices/invoice-summary";

interface ShowClientActionBarProps {
    reactivate(): void;
    placement: IPlacement;
    goToTab?: (section: Subpage) => void;
    hasActiveSubpage: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onFieldChange: (fieldName: string, newValue: any) => void;
}

export const ShowClientActionBar: React.FC<ShowClientActionBarProps> = props => {
    const { placement, reactivate, goToTab, hasActiveSubpage, onFieldChange } = props;
    const responsiveMode = useResponsiveMode();
    const communities = useMemo(() => {
        const clientCommunities = placement?.clientCommunities || [];
        return clientCommunities
            .filter(
                cc =>
                    cc.relationship === CommunityRelationshipType.featured ||
                    placement.chosenCommunityId === cc.community?.id,
            )
            .map(cc => cc.community);
    }, [placement?.clientCommunities, placement?.chosenCommunityId]);

    const { getListItemKey } = useCustomList(CustomListKey.ClientStatus);

    const clientStatus = getListItemKey(placement.statusListItemId);

    let scheduledClassName = "";

    switch (clientStatus) {
        case "moved":
        case "scheduled":
            scheduledClassName = " action-bar-accented-component";
            break;
    }

    const shouldShowActionBar =
        responsiveMode >= ResponsiveMode.large || !hasActiveSubpage;
    return (
        <div
            className={
                shouldShowActionBar
                    ? "opacity-100 max-h-96"
                    : "opacity-0 max-h-0 overflow-hidden"
            }>
            <div className="@container space-y-2 px-4 py-2">
                <div className="flex flex-wrap gap-2 md:gap-3">
                    <div>
                        <ActionBarStatus placement={placement} reactivate={reactivate} />
                    </div>
                    {clientStatus === "checkLater" ? (
                        <div>
                            <ActionBarDateComponent
                                className="check-back-later-date"
                                placement={placement}
                                datePropertyName="statusCheckLaterDate"
                                dateVal={placement.statusCheckLaterDate}
                                displayLabel="Check Back Later Date"
                            />
                        </div>
                    ) : null}
                    <div>
                        <ActionBarDateComponent
                            placement={placement}
                            datePropertyName="moveTimeline"
                            dateVal={placement.moveTimeline}
                            displayLabel="Estimated Move Timeline"
                        />
                    </div>
                    <div>
                        <ActionBarMoveCommunity
                            className={scheduledClassName}
                            placement={placement}
                            communities={communities}
                            goToTab={(section: Subpage) => goToTab(section)}
                        />
                    </div>
                    <div>
                        <ActionBarDateComponent
                            className={scheduledClassName}
                            placement={placement}
                            datePropertyName="moveTimelineActual"
                            dateVal={placement.moveTimelineActual}
                            displayLabel="Move Date"
                        />
                    </div>
                    <InvoiceSummary clientId={placement.id} />
                    <div>
                        <ActionBarAssignedTo placement={placement} />
                    </div>
                    <NgBylinePill
                        entity={placement}
                        mode="action-bar"
                        createdAtDateModified={(newDate: Date) =>
                            onFieldChange("dateAddedManualOverride", newDate)
                        }
                    />
                </div>
            </div>
        </div>
    );
};
